import ApplicationController from "../application_component_controller"

import URL from "hb/lib/url"
import { useListen } from "hb/insights/use_listen"
import { useOutlets } from "hb/insights/use_outlets"
import { DEFAULT_TS_PARAM } from "hb/insights/globals"
import Query, { DEFAULT_QUERY } from "hb/insights/query"
import { userTZ } from "hb/lib/date_helpers"
import { fetchTurbo } from "hb/lib/fetch"
import { without } from "hb/lib/collection_helpers"
import { deepEqual } from "hb/lib/utils"

export default class extends ApplicationController {
  static values = {
    querySessionId: String,
    searchPath: String,
    queryState: {type: Object, default: null},
    visState: {type: Object, default: null},
    useParamState: { type: Boolean, default: true },
    viewPath: String
  }

  connect() {
    useListen(this)

    // QueryConfig
    // Initialize queryState before useOutlets since outletsConnected calls search()
    //
    if(this.useParamStateValue) {
      this.queryState = this.queryStateFromParams()

      const query = URL.params().get("query")
      if(query === null || query === "") {
        URL.replaceParam("query", DEFAULT_QUERY)
      }
    } else {
      this.queryState = this.queryStateValue
    }

    useOutlets(this, { components: ["query"] })
    this.listenWindow("submitQuery", this.querySubmitted)
    this.listenWindow("queryTsChanged", this.queryTsChanged)
    this.listenWindow("queryTimezoneChanged", this.queryTimezoneChanged)
    this.listenWindow("queryStreamChanged", this.queryStreamChanged)

    // VisConfig
    //
    if(this.useParamStateValue) {
      const currVisState = this.visStateFromParams()
      this.visState = {
        view: currVisState.view || "table",
        [currVisState.view]: without(currVisState, "view")
      }
    } else {
      this.visState = {
        view: this.visStateValue?.view || "table",
        [this.visStateValue?.view || "table"]: this.visStateValue.chart_config || {}
      }
    }

    this.listenWindow("vis:viewChange", this.visViewChanged)
    this.listenWindow("vis:paramChange", this.visParamChanged)
    this.listenWindow("vis:groupParamChange", this.visGroupParamChanged)

    if(this.useParamStateValue) {
      this.listenWindow("urlchanged", this.urlChanged)
    }
  }

  outletsConnected() {
    this.search()
  }

  visStateFromParams() {
    const params = URL.paramsToObject(URL.removeParams(["query", "ts", "timezone", "stream_ids"]))
    if(typeof params.groups === "string") {
      try {
        params.groups = JSON.parse(params.groups)
      } catch(e) {
        console.error("Could not parse groups param", e)
        params.groups = undefined
      }
    }

    return params
  }

  emitVisUpdate() {
    this.emitWindow("updateVisState", {
      querySessionId: this.querySessionIdValue,
      view: this.visState.view,
      ...this.visState[this.visState.view]
    })
  }

  emitQueryUpdate() {
    this.emitWindow("updateQueryState", {
      querySessionId: this.querySessionIdValue,
      ...this.queryState
    })
  }

  get queryVisState() {
    const data = {
      query: this.queryState.query,
      vis: {
        view: this.visState.view,
        chart_config: this.visState[this.visState.view] || {}
      }
    }
    if(this.queryState.streamIds) {
      data.stream_ids = this.queryState.streamIds
    }
    return data
  }

  queryStateFromParams() {
    const params = URL.params()

    return {
      query: params.get("query") || DEFAULT_QUERY,
      ts: params.get("ts") || DEFAULT_TS_PARAM,
      timezone: params.get("timezone"),
      streamIds: params.has("stream_ids") ? JSON.parse(params.get("stream_ids")) : null
    }
  }

  visParamChanged({name, value}) {
    (this.visState[this.visState.view] ??= {})[name] = value

    this.useParamStateValue ? URL.updateParam(name, value) : null
    this.emitVisUpdate()
  }

  visGroupParamChanged({name, data}) {
    ((this.visState[this.visState.view] ??= {}).groups ??= {})[name] ??= {}
    this.visState[this.visState.view].groups[name] = {
      ...this.visState[this.visState.view].groups[name],
      ...data
    }

    this.useParamStateValue ? URL.updateParam("groups", JSON.stringify(this.visState[this.visState.view].groups)) : null
    this.emitVisUpdate()
  }

  urlChanged() {
    const nextQueryState = this.queryStateFromParams()
    if (!deepEqual(nextQueryState, this.queryState)) {
      this.queryState = nextQueryState
      this.emitQueryUpdate()
      this.search()
    }

    const nextVisState = this.visStateFromParams()
    if(nextVisState.groups !== undefined && typeof nextVisState.groups === "string") {
      nextVisState.groups = JSON.parse(nextVisState.groups)
    }
    const prevVisState = this.visState[nextVisState.view] || {}

    if (nextVisState.view !== this.visState.view) {
      this.visState[nextVisState.view] = without(nextVisState, "view")
      this.visState.view = nextVisState.view
      this.loadView()
      this.emitVisUpdate()
    } else if (!deepEqual(prevVisState, without(nextVisState, "view"))) {
      this.visState.view = nextVisState.view
      this.visState[this.visState.view] = without(nextVisState, "view")
      this.emitVisUpdate()
    }
  }

  querySubmitted({query, ts}) {
    let changed = {}
    if (query !== this.query) {
      this.queryState.query = query
      changed.query = query
    }
    if(ts && ts !== this.queryState.ts) {
      this.queryState.ts = ts
      changed.ts = ts
    }

    if(Object.keys(changed).length > 0) {
      if(this.useParamStateValue) {
        URL.update(URL.mergeParams(changed))
      }

      this.emitQueryUpdate()
    }

    this.search()
  }

  queryStreamChanged({streamIds}) {
    this.queryState.streamIds = streamIds
    this.useParamStateValue ? URL.updateParam("stream_ids", streamIds ? JSON.stringify(streamIds) : null) : null
  }

  queryTsChanged({ts}) {
    if (ts !== this.queryState.ts) {
      this.queryState.ts = ts
      this.useParamStateValue ? URL.updateParam("ts", ts) : null
      this.emitQueryUpdate()
      this.search()
    }
  }

  queryTimezoneChanged({timezone}) {
    if (timezone !== this.queryState.timezone) {
      this.queryState.timezone = timezone
      this.useParamStateValue ? URL.updateParam("timezone", timezone) : null
      this.emitQueryUpdate()
      this.search()
    }
  }

  async visViewChanged({view}) {
    const prevView = this.visState.view
    this.visState.view = view
    this.loadView()
    if(this.useParamStateValue) {
      const params = URL.params()
      URL.removeParams(Object.keys(this.visState[prevView] || {}), params)
      URL.mergeParams({
        ...(this.visState[view] || {}),
        view
      }, params)
      URL.update(params)
      this.emitVisUpdate()
    }
  }

  async loadView() {
    await fetchTurbo({
      path: this.viewPathValue,
      method: "GET",
      snakeCaseBody: false,
      params: {
        view: this.visState.view || "table",
        querySessionId: this.querySessionIdValue,
        chart_config: JSON.stringify(this.visState[this.visState.view] || {})
      }
    })
  }

  async search() {
    await Query.fetch({
      ...this.queryState,
      ts: this.queryState.ts || DEFAULT_TS_PARAM,
      timezone: this.queryState.timezone || userTZ(),
      querySessionId: this.querySessionIdValue,
      path: this.searchPathValue,
      source: "insights"
    })
  }
}
